import { notifications } from '@mantine/notifications';
import { useMutation, useSuspenseQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { ErrorIcon, Success } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useBackOrNavigate } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DetailsLayout } from '@apple/ui/layouts/DetailsLayout';
import { ServerValidationError } from '@apple/utils/api/errors';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { CustomerForm } from '@apple/features/customer/components/CustomerManagement/CustomerForm';
import { addCustomer } from '@apple/features/customer/management';
import { programsQueryOptions } from '@apple/features/program/queries';

export const Route = createFileRoute('/_authed/_admin/manage/customers_/add')({
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.CustomerManagement',
		});

		return {
			programsQueryOptions: programsQueryOptions(),
		};
	},
	loader: ({ context: { queryClient, programsQueryOptions } }) => {
		void queryClient.prefetchQuery(programsQueryOptions);
	},
	wrapInSuspense: true,
	component: CustomerRoute,
});

function CustomerRoute() {
	const { t } = useTranslation('customer');
	const { programsQueryOptions, queryClient } = Route.useRouteContext();

	const [programsQuery] = useSuspenseQueries({
		queries: [programsQueryOptions],
	});

	const goBackToList = useBackOrNavigate();

	const createCustomer = useMutation({
		mutationFn: addCustomer,
		onSuccess: async () => {
			notifications.show({
				color: 'green',
				message: t('add.success'),
				icon: <Success />,
			});
			await queryClient.invalidateQueries({ queryKey: ['customers-odata'] });
			await goBackToList('/manage/customers');
		},
		onError: (error: Error) => {
			if (!(error instanceof ServerValidationError)) {
				notifications.show({
					message: t('common:error.generic'),
					icon: <ErrorIcon />,
					color: 'red',
					autoClose: 5000,
				});
			}
		},
	});

	return (
		<DetailsLayout title={t('title.add')}>
			<CustomerForm
				isEdit={false}
				onClose={() => void goBackToList('/manage/customers')}
				onSaveCustomer={createCustomer.mutateAsync}
				programs={programsQuery.data}
			/>
		</DetailsLayout>
	);
}
